import { subscribe, getState }   from '../store';
import { socket }                from '../socket';
import { registerScreen }        from '../router';
import { escHtml }               from '../ui';
import type { Player, GameDefinition } from '../../../shared/types';

// ─── Game definitions (client-side display info) ──────────────────────────────
// Full logic lives server-side; client only needs display metadata

const GAME_META: Record<string, Pick<GameDefinition, 'name'> & { icon: string; description: string }> = {
  hangman:            { name: 'Hangman Race',        icon: '🪢', description: 'Guess the word before you run out of chances!' },
  trivia:             { name: 'Speed Trivia',         icon: '🧠', description: 'Answer fast — first to 3 right wins!' },
  tap_battle:         { name: 'Tap Battle',           icon: '👆', description: 'Tap as fast as you can. Most taps wins!' },
  shake_challenge:    { name: 'Shake Challenge',      icon: '📱', description: 'Shake your phone with everything you\'ve got!' },
  drawing:            { name: 'Drawing Showdown',     icon: '🎨', description: 'Draw the word — your team has to guess it!' },
  word_chain:         { name: 'Word Chain',           icon: '🔗', description: 'Each word must start with the last letter!' },
  emoji_pictionary:   { name: 'Emoji Pictionary',     icon: '🤔', description: 'Guess the phrase from emoji clues!' },
  rock_paper_scissors:{ name: 'Rock Paper Scissors',  icon: '✊', description: 'Best of three — pick your throw!' },
  music_buzzer:       { name: 'Music Buzzer',         icon: '🎵', description: 'Buzz in first when you know the song!' },
  reaction_tap:       { name: 'Reaction Tap',         icon: '⚡', description: 'Tap the moment you see the signal!' },
  memory_sequence:    { name: 'Memory Sequence',      icon: '🔢', description: 'Repeat the sequence — one wrong and you\'re out!' },
  tilt_maze:          { name: 'Tilt Maze',            icon: '🌀', description: 'Tilt your phone to guide the ball to the exit!' },
  token_steal:        { name: 'Token Steal',          icon: '🪙', description: 'Pick someone to steal their rescue token…' },
  token_grant:        { name: 'Token Grant',          icon: '🎁', description: 'Vote to give your token to someone deserving!' },
  token_gamble:       { name: 'Token Gamble',         icon: '🎰', description: 'Risk your token for a shot at double or nothing!' },
  token_shield:       { name: 'Token Shield',         icon: '🛡️', description: 'Protect yourself — block the next steal attempt!' },
};

const AUTO_ADVANCE_SECS = 30;

// ─── Template ─────────────────────────────────────────────────────────────────

function render(gameId: string, round: number, totalRounds: number, skipsRemaining: number): string {
  const meta = GAME_META[gameId] ?? { name: gameId, icon: '🎮', description: '' };

  return `
    <div class="instruction__topbar">
      <span class="instruction__round">Round ${round} / ${totalRounds}</span>
      <span class="instruction__skips">⏭ ${skipsRemaining} skips</span>
    </div>

    <div class="instruction__hero">
      <span class="instruction__next-label">Next Up</span>
      <span class="instruction__game-icon">${meta.icon}</span>
      <h1 class="instruction__game-name">${escHtml(meta.name)}</h1>
      <p class="instruction__game-desc">${escHtml(meta.description)}</p>
    </div>

    <div class="instruction__players">
      <p class="instruction__players-label">Players</p>
      <div id="instruction-player-list"></div>
    </div>

    <div class="instruction__footer">
      <div class="instruction__auto-bar">
        <div class="instruction__auto-bar__fill" id="auto-bar-fill"></div>
      </div>

      <div class="instruction__footer-row" id="instruction-actions">
        <!-- populated based on host/player role -->
      </div>
    </div>
  `;
}

// ─── Screen ───────────────────────────────────────────────────────────────────

let unsubscribe:   (() => void) | null = null;
let autoBarTimer:  ReturnType<typeof setInterval> | null = null;

registerScreen('instruction', {
  mount() {
    const el      = document.getElementById('screen-instruction')!;
    const { session, myPlayer } = getState();
    if (!session) return;

    const gameId = session.currentGameId ?? 'hangman';
    el.innerHTML = render(
      gameId,
      session.currentRound,
      session.totalRounds,
      session.skipsRemaining
    );

    renderPlayers(session.players);
    renderActions(!!myPlayer?.isHost, session.skipsRemaining);
    startAutoBar();

    // Reactive: update votes in real-time
    unsubscribe = subscribe(({ session: s }) => {
      if (!s || s.phase !== 'INSTRUCTION') return;
      renderPlayers(s.players);

      // Update skips count
      const skipsEl = document.querySelector('.instruction__skips');
      if (skipsEl) skipsEl.textContent = `⏭ ${s.skipsRemaining} skips`;

      // Update skip button state
      const skipBtn = document.getElementById('btn-skip') as HTMLButtonElement | null;
      if (skipBtn) skipBtn.disabled = s.skipsRemaining <= 0;
    });
  },

  unmount() {
    unsubscribe?.();
    unsubscribe = null;
    stopAutoBar();
  },
});

// ─── Player vote list ─────────────────────────────────────────────────────────

function renderPlayers(players: Record<string, Player>): void {
  const list = document.getElementById('instruction-player-list');
  if (!list) return;

  const sorted = Object.values(players).sort((a, b) => a.joinOrder - b.joinOrder);

  list.innerHTML = sorted.map(p => {
    const icon   = p.status === 'ghost' ? '💀' : p.isHost ? '👑' : '🟠';
    const vote   = p.vote === 'ready' ? '👍' : p.vote === 'skip' ? '👎' : '⏳';
    const status = p.vote ? (p.vote === 'ready' ? 'Ready' : 'Skip') : 'Waiting…';

    return `
      <div class="instruction__player" data-player-id="${p.id}" data-vote="${p.vote ?? ''}">
        <span>${icon}</span>
        <span class="instruction__player__name">${escHtml(p.username)}</span>
        <span class="instruction__player__vote">${vote}</span>
        <span class="instruction__player__status">${status}</span>
      </div>
    `;
  }).join('');
}

// ─── Action buttons ───────────────────────────────────────────────────────────

function renderActions(isHost: boolean, skipsRemaining: number): void {
  const actions = document.getElementById('instruction-actions');
  if (!actions) return;

  if (isHost) {
    // Host: Start + Skip
    actions.innerHTML = `
      <button class="btn btn--skip" id="btn-skip" ${skipsRemaining <= 0 ? 'disabled' : ''}>
        ⏭ Skip (${skipsRemaining})
      </button>
      <button class="btn btn--primary" id="btn-start">
        ▶ Start
      </button>
    `;

    document.getElementById('btn-start')!.addEventListener('click', () => {
      socket.send({ type: 'host_start' });
      stopAutoBar();
    });

    document.getElementById('btn-skip')!.addEventListener('click', () => {
      socket.send({ type: 'host_skip' });
      stopAutoBar();
      // Reset auto-bar for new game
      startAutoBar();
    });
  } else {
    // Player: Ready + Skip vote
    actions.innerHTML = `
      <button class="btn btn--outline" id="btn-vote-skip">
        👎 Skip
      </button>
      <button class="btn btn--primary" id="btn-vote-ready">
        👍 Ready
      </button>
    `;

    document.getElementById('btn-vote-ready')!.addEventListener('click', () => {
      socket.send({ type: 'player_vote', vote: 'ready' });
      lockVoteButtons('ready');
    });

    document.getElementById('btn-vote-skip')!.addEventListener('click', () => {
      socket.send({ type: 'player_vote', vote: 'skip' });
      lockVoteButtons('skip');
    });
  }
}

function lockVoteButtons(voted: 'ready' | 'skip'): void {
  const readyBtn = document.getElementById('btn-vote-ready') as HTMLButtonElement | null;
  const skipBtn  = document.getElementById('btn-vote-skip')  as HTMLButtonElement | null;
  if (readyBtn) { readyBtn.disabled = true; readyBtn.style.opacity = voted === 'ready' ? '1' : '0.3'; }
  if (skipBtn)  { skipBtn.disabled  = true; skipBtn.style.opacity  = voted === 'skip'  ? '1' : '0.3'; }
}

// ─── Auto-advance bar ────────────────────────────────────────────────────────

function startAutoBar(): void {
  stopAutoBar();
  const fill = document.getElementById('auto-bar-fill');
  if (!fill) return;

  let elapsed = 0;
  fill.style.width = '0%';

  autoBarTimer = setInterval(() => {
    elapsed++;
    fill.style.width = `${(elapsed / AUTO_ADVANCE_SECS) * 100}%`;
    if (elapsed >= AUTO_ADVANCE_SECS) stopAutoBar();
  }, 1000);
}

function stopAutoBar(): void {
  if (autoBarTimer) clearInterval(autoBarTimer);
  autoBarTimer = null;
}
